// LayoutImpressao.styles.ts
//
// Folha da aba "Layout de Impressão". Anatomia igual às outras sections de
// Lojas (MeiosPagamento, Site, Ambients): FieldsContainer como raiz,
// Header/HeaderContent/HeaderTitle/HeaderSubtitle, LoadingContainer,
// EmptyState e Aviso tonalizado.
//
// Aqui a folha cobre SÓ a moldura: cabeçalho, seletores, avisos e a caixa que
// hospeda o editor. O miolo (EditorLayoutComanda + PreviewComanda) continua com
// estilos inline de propósito — `ferramentas/gerarPreviewEstatico.tsx` renderiza
// esses dois com `renderToStaticMarkup`, sem ServerStyleSheet, então tudo que
// virasse styled-component sumiria do HTML de conferência.
//
// Tamanhos só na escada de tokens (`var(--sp-N)` / `var(--fs-N)`); o que não cai
// nos valores de --sp- vira `calc(var(--u) * N)`. Cores só de
// `props.theme.colors` — que hoje tem background, text, selection, modalColor e
// borderColor (o `colors.border` usado no repo resolve undefined; é a dívida
// vigente e não é consertada aqui).
import styled, { keyframes } from "styled-components";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-16);
  gap: var(--sp-16);
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
`;

export const HeaderContent = styled.div`
  flex: 1;
  min-width: calc(var(--u) * 260);
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  margin: 0 0 var(--sp-6) 0;
  font-size: var(--fs-3xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

// ===== SELETORES DO TOPO =====
export const BarraSeletores = styled.div`
  display: flex;
  align-items: flex-end;
  gap: var(--sp-16);
  flex-wrap: wrap;
  margin-bottom: var(--sp-16);
`;

export const CampoSeletor = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  min-width: calc(var(--u) * 240);
`;

export const RotuloCampo = styled.span`
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(props) => props.theme.colors.label};
`;

export const Seletor = styled.select`
  padding: var(--sp-8) var(--sp-10);
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.modalColor};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// ===== AVISO INLINE =====
// Vive aqui, na própria aba, e não no <MessageBox /> do Zustand: o MessageBox
// não está montado na tela de Configuração (só em Login e Register), então
// setMessageZustand não apareceria para ninguém.
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  margin-bottom: var(--sp-12);
  padding: var(--sp-10) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.4;
  border: 1px solid
    ${(props) =>
      props.$tom === "erro"
        ? "#be3028"
        : props.$tom === "atencao"
        ? "#8f5e07"
        : "#26792f"};
  background-color: ${(props) =>
    props.$tom === "erro"
      ? "#be302818"
      : props.$tom === "atencao"
      ? "#8f5e0718"
      : "#26792f18"};
  color: ${(props) => props.theme.colors.text};
  animation: ${fadeIn} 0.3s ease;
`;

// ===== CAIXA QUE HOSPEDA O EDITOR =====
// O editor tem fundo claro fixo (é a simulação do papel que manda no visual
// dele). A caixa só recorta e arredonda para ele não vazar do painel.
export const PainelEditor = styled.div`
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 12px;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;
`;

// ===== ESTADOS =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-16);
  padding: calc(var(--u) * 60);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: var(--sp-40);
  height: var(--sp-40);
  border: 4px solid ${(props) => props.theme.colors.selection + "30"};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

export const EmptyState = styled.div`
  border: 2px dashed ${(props) => props.theme.colors.selection + "50"};
  border-radius: 12px;
  padding: calc(var(--u) * 60) var(--sp-40);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-8);
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;
